import { Component, ElementRef, OnDestroy, AfterViewInit, ViewChild, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as THREE from 'three';
import { debounceTime, Subject } from 'rxjs';
// @ts-ignore
import CLOUDS from 'vanta/dist/vanta.clouds.min';
import { AuthService } from '../../../service/auth.service';
import { GlobalLoadingService } from '../../../shared/global-loading.service';

@Component({
  selector: 'app-vanta-cloud-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './vanta-cloud-login.component.html',
  styleUrls: ['./vanta-cloud-login.component.css']
})
export class VantaCloudLoginComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('vantaBg', { static: false }) vantaRef!: ElementRef;
  private vantaEffect: any;
  private isTransitioning = false;
  private formContainers: HTMLElement[] = [];

  // Injected services
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private globalLoading = inject(GlobalLoadingService);

  // Login form model
  loginModel = { username: '', password: '' };
  loginError: string | null = null;
  loginLoading = false;
  usernameValid = true;

  // Register form model
  registerModel = { username: '', email: '', password: '', confirmPassword: '', deviceId: '' };
  registerError: string | null = null;
  registerLoading = false;
  passwordStrength = 0;
  private passwordInput$ = new Subject<string>();
  usernameValidReg = true;
  emailValid = true;
  showRules = false;

  // Background settings for Vanta
  private nightSettings = {
    skyColor: 0x000000,
    cloudColor: 0xb6b6b6,
    cloudShadowColor: 0x7f7f7f,
    sunColor: 0x000000,
    sunGlareColor: 0x000000,
    sunlightColor: 0x000000,
    speed: 0.50
  };
  private daySettings = {
    skyColor: 0x51b4eb,
    cloudColor: 0xe2e4eb,
    cloudShadowColor: 0x6e8aa4,
    sunColor: 0xfcd520,
    sunGlareColor: 0xe3d12f,
    sunlightColor: 0xffce30,
    speed: 0.50
  };
  private morningSettings = {
    skyColor: 0x508eac,
    cloudColor: 0xc2c3d9,
    cloudShadowColor: 0xb9c0c8,
    sunColor: 0xffb400,
    sunGlareColor: 0xe6b317,
    sunlightColor: 0xfabe0a,
    speed: 0.50
  };
  private sunsetSettings = {
    skyColor: 0x11587a,
    cloudColor: 0x9c9daf,
    cloudShadowColor: 0xb3b1a8,
    sunColor: 0xff8700,
    sunGlareColor: 0xe6a816,
    sunlightColor: 0xfa890a,
    speed: 0.50
  };

  ngOnInit(): void {
    this.passwordInput$.pipe(debounceTime(250)).subscribe((pw) => {
      this.passwordStrength = this.evaluatePasswordStrength(pw);
    });

    const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
    if (
      this.authService.currentUserValue &&
      this.authService.currentUserValue.accessToken &&
      !this.authService.isTokenExpired()
    ) {
      const user = this.authService.currentUserValue?.user;
      if (user?.email) this.loginModel.username = user.email;
      else if (user?.username) this.loginModel.username = user.username;
      return;
    }
    this.loginLoading = true;
    this.globalLoading.show();
    this.authService.refreshToken().subscribe({
      next: () => {
        this.loginLoading = false;
        this.globalLoading.hide();
        const user = this.authService.currentUserValue?.user;
        if (user?.email) this.loginModel.username = user.email;
        else if (user?.username) this.loginModel.username = user.username;
      },
      error: () => {
        this.loginLoading = false;
        this.globalLoading.hide();
      }
    });
  }

  ngAfterViewInit(): void {
    // Get all form containers (login and register)
    this.formContainers = Array.from(this.vantaRef.nativeElement.querySelectorAll('.form-container'));
    const initialSettings = this.getTimeOfDayPreviousSettings();
    const currentSettings = this.getTimeOfDaySettings();

    this.vantaEffect = CLOUDS({
      el: this.vantaRef.nativeElement,
      THREE: THREE,
      ...initialSettings,
      mouseControls: true,
      touchControls: true,
      gyroControls: false,
      minHeight: 200.0,
      minWidth: 200.0
    });

    // Apply initial themes with no transition
    this.formContainers.forEach(container => this.setFormThemeClassToContainer(container, initialSettings));

    // Animate background and transition form theme
    setTimeout(() => {
      this.animateTransition(initialSettings, currentSettings, 4000);
      this.formContainers.forEach(container => {
        this.transitionFormThemeClassToContainer(container, initialSettings, currentSettings, 4000);
      });
    }, 50);

    this.initFormSwitching();
    this.setupFormValidation();
  }

  ngOnDestroy(): void {
    if (this.vantaEffect) this.vantaEffect.destroy();
  }

  private getTimeOfDaySettings(): any {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 10) return this.morningSettings;
    if (hour >= 10 && hour < 18) return this.daySettings;
    if (hour >= 18 && hour < 21) return this.sunsetSettings;
    return this.nightSettings;
  }

  private getTimeOfDayPreviousSettings(): any {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 10) return this.nightSettings;
    if (hour >= 10 && hour < 18) return this.morningSettings;
    if (hour >= 18 && hour < 21) return this.daySettings;
    return this.sunsetSettings;
  }

  private setFormThemeClassToContainer(container: HTMLElement, theme: any): void {
    container.classList.remove('form-morning', 'form-day', 'form-sunset', 'form-night');
    if (theme === this.morningSettings) container.classList.add('form-morning');
    else if (theme === this.daySettings) container.classList.add('form-day');
    else if (theme === this.sunsetSettings) container.classList.add('form-sunset');
    else container.classList.add('form-night');
  }

  private transitionFormThemeClassToContainer(container: HTMLElement, from: any, to: any, duration: number): void {
    this.setFormThemeClassToContainer(container, from);
    container.classList.add('theme-transition');
    setTimeout(() => {
      this.setFormThemeClassToContainer(container, to);
      setTimeout(() => {
        container.classList.remove('theme-transition');
      }, duration);
    }, 50);
  }

  private animateTransition(from: any, to: any, duration: number) {
    const startTime = performance.now();
    const animate = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const lerp = (start: number, end: number, t: number) => start + (end - start) * t;
      const lerpColor = (c1: number, c2: number, t: number) => {
        const r = Math.round(lerp((c1 >> 16) & 255, (c2 >> 16) & 255, t));
        const g = Math.round(lerp((c1 >> 8) & 255, (c2 >> 8) & 255, t));
        const b = Math.round(lerp(c1 & 255, c2 & 255, t));
        return (r << 16) + (g << 8) + b;
      };
      const newColors = {
        skyColor: lerpColor(from.skyColor, to.skyColor, progress),
        cloudColor: lerpColor(from.cloudColor, to.cloudColor, progress),
        cloudShadowColor: lerpColor(from.cloudShadowColor, to.cloudShadowColor, progress),
        sunColor: lerpColor(from.sunColor, to.sunColor, progress),
        sunGlareColor: lerpColor(from.sunGlareColor, to.sunGlareColor, progress),
        sunlightColor: lerpColor(from.sunlightColor, to.sunlightColor, progress),
        speed: lerp(from.speed, to.speed, progress)
      };
      if (this.vantaEffect) this.vantaEffect.setOptions(newColors);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }

  private initFormSwitching(): void {
    const loginForm = document.getElementById('loginForm');
    const registerForm = document.getElementById('registerForm');
    const switchToRegister = document.getElementById('switchToRegister');
    const switchToLogin = document.getElementById('switchToLogin');
    switchToRegister?.addEventListener('click', () => {
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      loginForm?.classList.add('slide-out-left');
      loginForm?.classList.remove('active');
      setTimeout(() => {
        registerForm?.classList.remove('slide-in-left');
        registerForm?.classList.add('active');
        setTimeout(() => {
          this.isTransitioning = false;
        }, 800);
      }, 100);
    });
    switchToLogin?.addEventListener('click', () => {
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      registerForm?.classList.add('slide-out-left');
      registerForm?.classList.remove('active');
      setTimeout(() => {
        loginForm?.classList.remove('slide-out-left');
        loginForm?.classList.add('active');
        setTimeout(() => {
          this.isTransitioning = false;
        }, 800);
      }, 100);
    });
  }

  private setupFormValidation(): void {
    const loginFormElement = document.getElementById('loginFormElement') as HTMLFormElement;
    const registerFormElement = document.getElementById('registerFormElement') as HTMLFormElement;
    const passwordField = document.getElementById('registerPassword') as HTMLInputElement;
    const confirmPasswordField = document.getElementById('registerConfirmPassword') as HTMLInputElement;
    const strengthFill = document.getElementById('passwordStrengthFill') as HTMLElement;
    const passwordStrengthContainer = document.getElementById('passwordStrengthContainer');
    const togglePasswordBtn = document.getElementById('togglePassword');
    const toggleConfirmPasswordBtn = document.getElementById('toggleConfirmPassword');
    const rulesList = {
      length: document.getElementById('pwLength'),
      upper: document.getElementById('pwUpper'),
      number: document.getElementById('pwNumber'),
      symbol: document.getElementById('pwSymbol'),
    };

    passwordStrengthContainer?.classList.add('hide');
    passwordField?.addEventListener('focus', () => {
      passwordStrengthContainer?.classList.remove('hide');
    });
    passwordField?.addEventListener('blur', () => {
      if (passwordField.value === '') {
        passwordStrengthContainer?.classList.add('hide');
      }
    });

    togglePasswordBtn?.addEventListener('click', () => {
      const img = togglePasswordBtn.querySelector('img') as HTMLImageElement;
      if (passwordField.type === 'password') {
        passwordField.type = 'text';
        if (img) img.src = 'login/hide-password-eye-royalty.png';
      } else {
        passwordField.type = 'password';
        if (img) img.src = 'login/password-eye-royalty.png';
      }
    });

    toggleConfirmPasswordBtn?.addEventListener('click', () => {
      const img = toggleConfirmPasswordBtn.querySelector('img') as HTMLImageElement;
      if (confirmPasswordField.type === 'password') {
        confirmPasswordField.type = 'text';
        if (img) img.src = 'login/hide-password-eye-royalty.png';
      } else {
        confirmPasswordField.type = 'password';
        if (img) img.src = 'login/password-eye-royalty.png';
      }
    });

    passwordField?.addEventListener('input', () => {
      const val = passwordField.value;
      const hasLength = val.length >= 8;
      const hasUpper = /[A-Z]/.test(val);
      const hasNumber = /\d/.test(val);
      const hasSymbol = /[\W_]/.test(val);
      let passedCount = [hasLength, hasUpper, hasNumber, hasSymbol].filter(Boolean).length;
      let percent = passedCount * 25;
      strengthFill.style.width = percent + '%';
      ['level-2', 'level-3', 'level-4'].forEach((cls) => strengthFill.classList.remove(cls));
      if (passedCount > 1) strengthFill.classList.add('level-2');
      if (passedCount > 2) strengthFill.classList.add('level-3');
      if (passedCount > 3) strengthFill.classList.add('level-4');
      if (hasLength) rulesList.length?.classList.add('passed'); else rulesList.length?.classList.remove('passed');
      if (hasUpper) rulesList.upper?.classList.add('passed'); else rulesList.upper?.classList.remove('passed');
      if (hasNumber) rulesList.number?.classList.add('passed'); else rulesList.number?.classList.remove('passed');
      if (hasSymbol) rulesList.symbol?.classList.add('passed'); else rulesList.symbol?.classList.remove('passed');
    });

    loginFormElement?.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = (document.getElementById('loginEmail') as HTMLInputElement).value;
      const password = (document.getElementById('loginPassword') as HTMLInputElement).value;
      let isValid = true;
      if (!this.validateEmail(email)) {
        this.showError('loginEmail', 'loginEmailError', true);
        isValid = false;
      } else {
        this.showError('loginEmail', 'loginEmailError', false);
      }
      if (password.length === 0) {
        this.showError('loginPassword', 'loginPasswordError', true);
        isValid = false;
      } else {
        this.showError('loginPassword', 'loginPasswordError', false);
      }
      if (isValid) {
        this.onLoginSubmit();
      }
    });

    registerFormElement?.addEventListener('submit', (e) => {
      e.preventDefault();
      const name = (document.getElementById('registerName') as HTMLInputElement).value;
      const email = (document.getElementById('registerEmail') as HTMLInputElement).value;
      const password = (document.getElementById('registerPassword') as HTMLInputElement).value;
      const confirmPassword = (document.getElementById('registerConfirmPassword') as HTMLInputElement).value;
      const deviceId = (document.getElementById('registerDeviceId') as HTMLInputElement).value;
      let isValid = true;
      if (name.length === 0) {
        this.showError('registerName', 'registerNameError', true);
        isValid = false;
      } else {
        this.showError('registerName', 'registerNameError', false);
      }
      if (!this.validateEmail(email)) {
        this.showError('registerEmail', 'registerEmailError', true);
        isValid = false;
      } else {
        this.showError('registerEmail', 'registerEmailError', false);
      }
      const hasLength = password.length >= 8;
      const hasUpper = /[A-Z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSymbol = /[\W_]/.test(password);
      if (!(hasLength && hasUpper && hasNumber && hasSymbol)) {
        this.showError('registerPassword', 'registerPasswordError', true);
        isValid = false;
      } else {
        this.showError('registerPassword', 'registerPasswordError', false);
      }
      if (password !== confirmPassword) {
        this.showError('registerConfirmPassword', 'registerConfirmPasswordError', true);
        isValid = false;
      } else {
        this.showError('registerConfirmPassword', 'registerConfirmPasswordError', false);
      }
      if (deviceId.length === 0) {
        this.showError('registerDeviceId', 'registerDeviceIdError', true);
        isValid = false;
      } else {
        this.showError('registerDeviceId', 'registerDeviceIdError', false);
      }
      if (isValid) {
        this.onRegisterSubmit(name, email, password, deviceId);
      }
    });
  }

  // Login submission handler
  private onLoginSubmit(): void {
    this.loginError = null;
    this.loginLoading = true;
    this.globalLoading.show();
    const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
    this.authService.login(this.loginModel.username, this.loginModel.password).subscribe({
      next: () => {
        this.loginLoading = false;
        this.globalLoading.hide();
        this.showSuccess('loginSuccess', 'Welcome back! Login successful.');
        setTimeout(() => {
          this.router.navigateByUrl(returnUrl);
        }, 1500);
      },
      error: (err) => {
        this.loginLoading = false;
        this.globalLoading.hide();
        this.loginError = err?.error?.message || '';
        this.showSuccess('loginSuccess', 'Login failed, check input data.');
      },
    });
  }

  // Register submission handler
  private onRegisterSubmit(username: string, email: string, password: string, deviceId: string): void {
    this.registerError = null;
    this.registerLoading = true;
    this.globalLoading.show();
    this.authService.register({ username, email, password, deviceId }).subscribe({
      next: () => {
        this.registerLoading = false;
        this.globalLoading.hide();
        this.showSuccess('registerSuccess', 'Account created! Welcome aboard.');
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 1500);
      },
      error: (err) => {
        this.registerLoading = false;
        this.globalLoading.hide();
        this.registerError = err?.error?.message || 'Registration was unsuccessful.';
        this.showError('registerForm', 'registerFormError', true);
      },
    });
  }

  // Validation helpers
  private validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private evaluatePasswordStrength(pw: string): number {
    let score = 0;
    if (!pw) return 0;
    if (pw.length >= 8) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  }

  private showError(inputId: string, errorId: string, show: boolean): void {
    const input = document.getElementById(inputId);
    const error = document.getElementById(errorId);
    if (show) {
      input?.classList.add('error');
      error?.classList.add('show');
    } else {
      input?.classList.remove('error');
      error?.classList.remove('show');
    }
  }

  private showSuccess(elementId: string, message: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.textContent = message;
      element.classList.add('show');
      setTimeout(() => {
        element.classList.remove('show');
      }, 5000);
    }
  }

  // Public methods for template
  onLoginUsernameInput(value: string): void {
    this.usernameValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  onRegisterUsernameInput(value: string): void {
    this.usernameValidReg = !!value && value.length >= 3;
  }

  onRegisterEmailInput(value: string): void {
    this.emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  onPasswordInput(): void {
    this.passwordInput$.next(this.registerModel.password || '');
  }

  toggleRules(open?: boolean): void {
    this.showRules = typeof open === 'boolean' ? open : !this.showRules;
  }

  get passwordsMatch(): boolean {
    return this.registerModel.password === this.registerModel.confirmPassword;
  }
}